import { X, User, TrendingUp, Euro } from 'lucide-react';
import { NegotiatorTransaction } from '../../types/transaction';
import { formatCurrency } from '../../utils/format';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useState } from 'react';

interface NegotiatorDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  negotiatorName: string;
  transactions: NegotiatorTransaction[];
  agencyName?: string;
  startDate?: string;
  endDate?: string;
}

export default function NegotiatorDetailsModal({
  isOpen,
  onClose,
  negotiatorName,
  transactions,
  agencyName,
  startDate,
  endDate
}: NegotiatorDetailsModalProps) {
  useEscapeKey(() => {
    if (isOpen) {
      onClose();
    }
  });

  const [showNonRealized, setShowNonRealized] = useState(false);

  if (!isOpen) return null;

  const formatDate = (d?: string) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'potentiel':
        return 'bg-yellow-100 text-yellow-800';
      case 'compromis':
        return 'bg-blue-100 text-blue-800';
      case 'acte':
        return 'bg-green-100 text-green-800';
      case 'encaisse':
        return 'bg-purple-100 text-purple-800';
      case 'cloture':
        return 'bg-gray-100 text-gray-800';
      case 'annule':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'vendeur':
        return 'bg-blue-100 text-blue-800';
      case 'apporteur':
        return 'bg-green-100 text-green-800';
      case 'vendeur_apporteur':
        return 'bg-purple-100 text-purple-800';
      case 'reco':
        return 'bg-amber-100 text-amber-800';
      case 'autre':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-purple-100 text-purple-800';
    }
  };

  // Séparer les transactions en potentiel, annulées et les autres
  const potentialTransactions = transactions.filter(t => t.status === 'potentiel');
  const cancelledTransactions = transactions.filter(t => t.status === 'annule');
  const otherTransactions = transactions.filter(t => t.status !== 'potentiel' && t.status !== 'annule');

  // Calculer les totaux (sans compter les transactions annulées)
  const totalPotential = potentialTransactions.reduce((sum, t) => sum + t.final_revenue, 0);
  const totalCommission = otherTransactions.reduce((sum, t) => sum + t.final_revenue, 0);

  // Calculer les totaux par rôle (uniquement pour les transactions à partir de compromis, sans les annulées)
  const totals = otherTransactions.reduce((acc, t) => {
    switch (t.role) {
      case 'vendeur':
        acc.vendeur += t.final_revenue;
        break;
      case 'apporteur':
        acc.apporteur += t.final_revenue;
        break;
      case 'vendeur_apporteur':
        acc.vendeur_apporteur += t.final_revenue;
        break;
      case 'reco':
        acc.reco += t.final_revenue;
        break;
      case 'autre':
        acc.autre += t.final_revenue;
        break;
    }
    return acc;
  }, { vendeur_apporteur: 0, vendeur: 0, apporteur: 0, reco: 0, autre: 0 });

  // Colonne Agence affichée quand la modale n'est pas déjà limitée à une agence
  // et qu'au moins une ligne porte une agence (négociateur multi-agences)
  const showAgencyColumn = !agencyName && transactions.some(t => t.agency);

  const thCls = "px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider";
  const tdCls = "px-4 py-3 whitespace-nowrap text-sm";

  const transactionTableHead = (withStatus: boolean) => (
    <thead className="bg-slate-50 border-b border-slate-200">
      <tr>
        <th className={thCls}>Date compromis</th>
        <th className={thCls}>Vendeur</th>
        <th className={thCls}>Acquéreur</th>
        <th className={thCls}>Prix de vente</th>
        <th className={thCls}>Honoraires HT</th>
        {showAgencyColumn && <th className={thCls}>Agence</th>}
        <th className={thCls}>Rôle</th>
        <th className={thCls}>%</th>
        <th className={thCls}>Base</th>
        <th className={thCls}>Rétrocessions</th>
        <th className={thCls}>CA</th>
        {withStatus && <th className={thCls}>Statut</th>}
      </tr>
    </thead>
  );

  // Un négociateur peut avoir deux lignes sur la même transaction (une par agence) :
  // la clé doit inclure l'agence et le rôle pour rester unique
  const rowKey = (t: NegotiatorTransaction, index: number) =>
    `${t.id}-${t.agency ?? ''}-${t.role}-${index}`;

  const renderTransactionRow = (transaction: NegotiatorTransaction, withStatus: boolean, tinted?: boolean, index = 0) => (
    <tr key={rowKey(transaction, index)} className={`border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors ${tinted ? 'bg-red-50/20' : ''}`}>
      <td className={`${tdCls} font-medium text-slate-700`}>{formatDate(transaction.compromise_date)}</td>
      <td className={`${tdCls} text-slate-600`}>{transaction.seller}</td>
      <td className={`${tdCls} text-slate-600`}>{transaction.buyer}</td>
      <td className={`${tdCls} text-slate-700`}>{formatCurrency(transaction.sale_price)}</td>
      <td className={`${tdCls} text-slate-700`}>{formatCurrency(transaction.fees_without_tax)}</td>
      {showAgencyColumn && (
        <td className={`${tdCls} text-slate-600`}>{transaction.agency || '—'}</td>
      )}
      <td className={tdCls}>
        <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getRoleBadgeClass(transaction.role)}`}>
          {transaction.role}
        </span>
      </td>
      <td className={`${tdCls} text-slate-600`}>{transaction.percentage}%</td>
      <td className={`${tdCls} text-slate-700`}>{formatCurrency(transaction.base_revenue)}</td>
      <td className={`${tdCls} font-medium text-red-500`}>
        {transaction.retrocessions.length > 0
          ? `-${formatCurrency(transaction.retrocessions.reduce((s, r) => s + r.amount, 0))}`
          : <span className="text-slate-300">—</span>
        }
      </td>
      <td className={`${tdCls} font-bold ${withStatus ? 'text-emerald-600' : 'text-indigo-600'}`}>
        {formatCurrency(transaction.final_revenue)}
      </td>
      {withStatus && (
        <td className={tdCls}>
          <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getStatusBadgeClass(transaction.status)}`}>
            {transaction.status}
          </span>
        </td>
      )}
    </tr>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl relative max-h-[92vh] flex flex-col shadow-2xl">

        {/* ── Modal Header ── */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <User size={20} className="text-white/80" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white tracking-tight">
                  Détails des transactions
                </h2>
                <p className="text-sm text-slate-300 mt-0.5">{negotiatorName}</p>
                {(agencyName || (startDate && endDate)) && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    {agencyName ? `${agencyName}` : ''}
                    {agencyName && startDate ? ' — ' : ''}
                    {startDate && endDate ? `${formatDate(startDate)} au ${formatDate(endDate)}` : ''}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">

          {/* Summary + toggle */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-xl text-sm text-slate-600">
              <span className="font-semibold text-slate-800">{transactions.length}</span>
              transaction{transactions.length > 1 ? 's' : ''}
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-100 rounded-xl text-sm">
              <TrendingUp size={13} className="text-amber-600" />
              <span className="text-amber-700">Potentiel :</span>
              <span className="font-semibold text-amber-800">{formatCurrency(totalPotential)}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-xl text-sm">
              <Euro size={13} className="text-emerald-600" />
              <span className="text-emerald-700">Réalisé :</span>
              <span className="font-semibold text-emerald-800">{formatCurrency(totalCommission)}</span>
            </div>
            <label className="ml-auto flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <div
                onClick={() => setShowNonRealized(v => !v)}
                className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${showNonRealized ? 'bg-indigo-500' : 'bg-slate-200'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${showNonRealized ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
              Potentiel / annulé
            </label>
          </div>

          {/* CA Breakdown */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
              <Euro size={15} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Répartition du chiffre d'affaires</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-0 divide-x divide-slate-100">
              {[
                { label: 'CA 100%',     value: totals.vendeur_apporteur, color: 'text-violet-700',  bg: 'bg-violet-50' },
                { label: 'CA Vendeur',  value: totals.vendeur,           color: 'text-blue-700',    bg: 'bg-blue-50' },
                { label: 'CA Apporteur',value: totals.apporteur,         color: 'text-emerald-700', bg: 'bg-emerald-50' },
                { label: 'CA Reco',     value: totals.reco,              color: 'text-amber-700',   bg: 'bg-amber-50' },
                { label: 'CA Autre',    value: totals.autre,             color: 'text-slate-600',   bg: 'bg-slate-50' },
              ].map(item => (
                <div key={item.label} className={`${item.bg} p-4 text-center`}>
                  <p className={`text-xs font-medium ${item.color} mb-1`}>{item.label}</p>
                  <p className={`text-base font-bold ${item.color}`}>{formatCurrency(item.value)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Transactions en potentiel */}
          {showNonRealized && potentialTransactions.length > 0 && (
            <div className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3.5 bg-amber-50 border-b border-amber-100">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-sm font-semibold text-amber-800">
                  Transactions en potentiel ({potentialTransactions.length})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  {transactionTableHead(false)}
                  <tbody className="bg-white">
                    {potentialTransactions.map((t, i) => renderTransactionRow(t, false, false, i))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Transactions réalisées */}
          {otherTransactions.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3.5 bg-emerald-50 border-b border-emerald-100">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-sm font-semibold text-emerald-800">
                  Transactions réalisées ({otherTransactions.length})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  {transactionTableHead(true)}
                  <tbody className="bg-white">
                    {otherTransactions.map((t, i) => renderTransactionRow(t, true, false, i))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Transactions annulées */}
          {showNonRealized && cancelledTransactions.length > 0 && (
            <div className="bg-white rounded-2xl border border-red-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3.5 bg-red-50 border-b border-red-100">
                <div className="w-2 h-2 rounded-full bg-red-400" />
                <span className="text-sm font-semibold text-red-800">
                  Transactions annulées ({cancelledTransactions.length})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className={thCls}>Date compromis</th>
                      <th className={thCls}>Vendeur</th>
                      <th className={thCls}>Acquéreur</th>
                      <th className={thCls}>Prix de vente</th>
                      <th className={thCls}>Honoraires HT</th>
                      <th className={thCls}>Rôle</th>
                      <th className={thCls}>%</th>
                      <th className={thCls}>Statut</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {cancelledTransactions.map((transaction, i) => (
                      <tr key={rowKey(transaction, i)} className="border-b border-slate-100 last:border-0 bg-red-50/20">
                        <td className={`${tdCls} font-medium text-slate-700`}>{formatDate(transaction.compromise_date)}</td>
                        <td className={`${tdCls} text-slate-600`}>{transaction.seller}</td>
                        <td className={`${tdCls} text-slate-600`}>{transaction.buyer}</td>
                        <td className={`${tdCls} text-slate-700`}>{formatCurrency(transaction.sale_price)}</td>
                        <td className={`${tdCls} text-slate-700`}>{formatCurrency(transaction.fees_without_tax)}</td>
                        <td className={tdCls}>
                          <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getRoleBadgeClass(transaction.role)}`}>
                            {transaction.role}
                          </span>
                        </td>
                        <td className={`${tdCls} text-slate-600`}>{transaction.percentage}%</td>
                        <td className={tdCls}>
                          <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getStatusBadgeClass(transaction.status)}`}>
                            {transaction.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}